import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { KeycloakAdminApi, RealmRepresentation } from '../../services/keycloak-admin-api';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ToggleableOption } from '../toggleable-option/toggleable-option';

@Component({
  selector: 'app-realm-attributes-form',
  imports: [FormsModule, ReactiveFormsModule, RouterLink, ToggleableOption],
  templateUrl: './realm-attributes-form.html',
  styleUrl: './realm-attributes-form.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RealmAttributesForm implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private fb = inject(FormBuilder);

  realmForm: FormGroup;
  isLoading = false;
  isSaving = false;
  realmData: RealmRepresentation | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // Password visibility toggle
  protected showClientSecret = signal(false);

  constructor() {
    this.realmForm = this.fb.group({
      azureGroupSync: this.fb.group({
        enabled: [false],
        'client-id': ['', Validators.required],
        'client-secret': ['', Validators.required],
        'tenant-id': ['', Validators.required],
        'parent-group': [''],
      }),
    });
  }

  ngOnInit(): void {
    this.loadRealmData();
  }

  loadRealmData(): void {
    this.isLoading = true;
    this.keycloakApi
      .getRealmDetails()
      .then((data) => {
        this.realmData = data;

        // Initialize form with existing values
        if (data.attributes) {
          this.realmForm.reset({
            azureGroupSync: {
              enabled: data.attributes['azure.group-sync.enabled'] === 'true',
              'client-id': data.attributes['azure.group-sync.client-id'] || '',
              'client-secret': data.attributes['azure.group-sync.client-secret'] || '',
              'tenant-id': data.attributes['azure.group-sync.tenant-id'] || '',
              'parent-group': data.attributes['azure.group-sync.parent-group'] || '',
            },
          });
        }

        this.isLoading = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load realm data: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  saveRealmAttributes(): void {
    if (this.realmForm.invalid) {
      this.markFormGroupTouched(this.realmForm);
      return;
    }

    this.isSaving = true;

    // Only send the changed attributes
    const formValues = this.realmForm.value;
    const updatedRealmData: RealmRepresentation = {
      attributes: {
        'azure.group-sync.enabled': formValues.azureGroupSync['enabled'] ? 'true' : 'false',
        'azure.group-sync.client-id': formValues.azureGroupSync['client-id'],
        'azure.group-sync.client-secret': formValues.azureGroupSync['client-secret'],
        'azure.group-sync.tenant-id': formValues.azureGroupSync['tenant-id'],
        'azure.group-sync.parent-group': formValues.azureGroupSync['parent-group'] || '',
      },
    };

    this.keycloakApi
      .updateRealmAttributes(updatedRealmData)
      .then(() => {
        this.successMessage = 'Realm attributes updated successfully';
        this.isSaving = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to update realm attributes: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Helper to mark all form controls as touched
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control: AbstractControl) => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  // Toggle client secret visibility
  toggleClientSecretVisibility(): void {
    this.showClientSecret.update((show) => !show);
  }
}
